import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { VideoCard } from '../components/VideoCard';
import { getPendingVideos, getAdminStats, approveVideo, updateVideoStatus, getAllPayouts, updatePayoutStatus, getAllUsers, updateUserRole, deleteUser } from '../api';
import { CheckCircle, XCircle, Users, Video, UserCheck, Clock, Search, Shield, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingVideos, setPendingVideos] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('videos');

  // Users state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'superadmin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [videosRes, statsRes, payoutsRes] = await Promise.all([
        getPendingVideos(),
        getAdminStats(),
        getAllPayouts()
      ]);
      setPendingVideos(videosRes.data.videos);
      setStats(statsRes.data);
      setPayouts(payoutsRes.data.payouts);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = {};
      if (userSearch) params.search = userSearch;
      if (userRoleFilter) params.role = userRoleFilter;
      const res = await getAllUsers(params);
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, userRoleFilter]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('Rôle mis à jour');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Supprimer l'utilisateur "${username}" ? Cette action est irréversible.`)) return;
    try {
      await deleteUser(userId);
      toast.success('Utilisateur supprimé');
      fetchUsers();
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleApprove = async (videoId) => {
    try {
      await approveVideo(videoId);
      toast.success('Vidéo approuvée');
      fetchData();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleStatusChange = async (videoId, newStatus) => {
    try {
      await updateVideoStatus(videoId, newStatus);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handlePayoutUpdate = async (payoutId, newStatus) => {
    try {
      await updatePayoutStatus(payoutId, newStatus);
      toast.success('Statut du payout mis à jour');
      fetchData();
    } catch (error) {
      console.error('Failed to update payout:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (!user || loading) return null;

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8">Administration</h1>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Users className="w-4 h-4" />
                <span className="text-xs font-medium">Utilisateurs</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_users}</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Video className="w-4 h-4" />
                <span className="text-xs font-medium">Vidéos</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_videos}</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <UserCheck className="w-4 h-4" />
                <span className="text-xs font-medium">Créateurs</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_creators}</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">En attente</span>
              </div>
              <p className="text-2xl font-bold">{stats.pending_videos}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('videos')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'videos'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Vidéos en attente ({pendingVideos.length})
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'payouts'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Demandes de paiement ({payouts.length})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <span className="flex items-center gap-1"><Users className="w-4 h-4" /> Utilisateurs ({stats?.total_users ?? 0})</span>
          </button>
        </div>

        {/* Pending Approvals */}
        {activeTab === 'videos' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              Vidéos en attente d'approbation ({pendingVideos.length})
            </h2>

          {pendingVideos.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <p className="text-muted-foreground">Aucune vidéo en attente</p>
            </div>
          ) : (
            <div
              className="space-y-4"
              data-testid="admin-approvals-table"
            >
              {pendingVideos.map((video) => (
                <div
                  key={video.id}
                  className="bg-card border border-border rounded-xl p-4 flex flex-col lg:flex-row gap-4"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{video.title}</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Par {video.creator_username}
                    </p>
                    <p className="text-sm line-clamp-2">{video.description}</p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <select
                      onChange={(e) => handleStatusChange(video.id, e.target.value)}
                      defaultValue={video.status}
                      className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
                    >
                      <option value="new">Nouveau</option>
                      <option value="trending">Tendance</option>
                      <option value="featured">À la Une</option>
                      <option value="soon">Bientôt</option>
                    </select>

                    <button
                      onClick={() => handleApprove(video.id)}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      data-testid="admin-bulk-approve-button"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approuver
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Payout Management */}
        {activeTab === 'payouts' && (          <div>
            <h2 className="text-xl font-semibold mb-4" data-testid="admin-payouts-table">
              Demandes de paiement ({payouts.length})
            </h2>

            {payouts.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-xl">
                <p className="text-muted-foreground">Aucune demande de paiement</p>
              </div>
            ) : (
              <div className="space-y-4">
                {payouts.map((payout) => (
                  <div
                    key={payout.id}
                    className="bg-card border border-border rounded-xl p-4"
                  >
                    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-lg">{payout.amount} CDF</p>
                        <p className="text-sm text-muted-foreground">
                          Créateur: {payout.creator_username}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Provider: {payout.provider} • Tél: {payout.phone_number}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Demandé le: {new Date(payout.created_at).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs">
                          Statut actuel:{' '}
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              payout.status === 'completed'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                : payout.status === 'rejected'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                : payout.status === 'processing'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                            }`}
                          >
                            {payout.status}
                          </span>
                        </p>
                      </div>

                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <select
                          value={payout.status}
                          onChange={(e) => handlePayoutUpdate(payout.id, e.target.value)}
                          className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        >
                          <option value="pending">En attente</option>
                          <option value="approved">Approuvé</option>
                          <option value="processing">En cours</option>
                          <option value="completed">Complété</option>
                          <option value="rejected">Rejeté</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* User Management */}
        {activeTab === 'users' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              Gestion des utilisateurs ({usersTotal})
            </h2>

            {/* Filtres */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                  className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <select
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value)}
                className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
              >
                <option value="">Tous les rôles</option>
                <option value="viewer">Viewer</option>
                <option value="creator">Créateur</option>
                <option value="superadmin">Super Admin</option>
              </select>
              <button
                onClick={fetchUsers}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Rechercher
              </button>
            </div>

            {usersLoading ? (
              <div className="text-center py-12 text-muted-foreground">Chargement…</div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-xl">
                <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Utilisateur</th>
                      <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Email</th>
                      <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Points</th>
                      <th className="text-left px-4 py-3 font-medium">Rôle</th>
                      <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Inscrit le</th>
                      <th className="text-right px-4 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map((u) => (
                      <tr key={u.id} className="bg-card hover:bg-muted/40 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium">{u.username}</div>
                          <div className="text-xs text-muted-foreground md:hidden">{u.email}</div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{u.email}</td>
                        <td className="px-4 py-3 hidden sm:table-cell">{u.points}</td>
                        <td className="px-4 py-3">
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary ${
                              u.role === 'superadmin'
                                ? 'bg-purple-500/20 text-purple-500'
                                : u.role === 'creator'
                                ? 'bg-blue-500/20 text-blue-500'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            <option value="viewer">Viewer</option>
                            <option value="creator">Créateur</option>
                            <option value="superadmin">Super Admin</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs hidden lg:table-cell">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleDeleteUser(u.id, u.username)}
                            className="p-1.5 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Supprimer l'utilisateur"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
